import React, { useState, useRef, useEffect } from 'react';
import { getDatabase, ref, set, remove } from 'firebase/database';
import html2pdf from 'html2pdf.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Delete, Download, Eye, Edit } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm, message } from 'antd';
import QRCode from 'qrcode';
import app from '../../firebase.js';

// Import images
import edaplogo from "../../img/edaplogo.png";
import crlogo from "../../img/crlogo.png";
import rectorSig from "../../UNCR/img/rector.png";
import perezSig from "../../img/perez.png";
import bapSig from "../../img/bap_sig.png";
import './UNILISUNDDiplomaSans.css';

const UNILISUNDDiplomaSansTemplate = () => {
    const [formData, setFormData] = useState({
        noms: '',
        dateNaissance: '',
        lieuNaissance: '',
        province: '',
        pays: '',
        department: '',
        specialite: '',
        departmentEn: '',
        specialiteEn: '',
        titreSuffix: '',
        englishTitle: '',
        gradeType: 'Doctorat',
        gradeTypeCustom: '',
        mention: 'Très Honorable',
        anneeAcademique: '',
        numeroDoc: '',
        dateEmission: new Date().toISOString().split('T')[0],
        language: 'Français + English'
    });
    const [documents, setDocuments] = useState([]);
    const [isLoadingDocs, setIsLoadingDocs] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [qrCodeData, setQrCodeData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const db = getDatabase(app);

    useEffect(() => {
        let cancelled = false;

        const loadDocuments = async () => {
            setIsLoadingDocs(true);
            try {
                const rootUrl = ref(db, 'unilis-und-diploma-sans/EDAP').toString();
                const response = await fetch(`${rootUrl}.json?shallow=true`);
                if (!response.ok) throw new Error('Failed to load documents');
                const keysMap = await response.json();
                const keys = keysMap ? Object.keys(keysMap) : [];
                if (cancelled) return;
                if (keys.length === 0) {
                    setDocuments([]);
                    return;
                }

                let nextIndex = 0;
                const workers = Array.from({ length: Math.min(6, keys.length) }, async () => {
                    while (nextIndex < keys.length) {
                        const key = keys[nextIndex];
                        nextIndex += 1;
                        try {
                            const docResponse = await fetch(`${rootUrl}/${encodeURIComponent(key)}.json`);
                            if (!docResponse.ok) continue;
                            const data = await docResponse.json();
                            if (cancelled || !data) continue;
                            setDocuments((prev) => (
                                prev.some((item) => item.id === key) ? prev : [...prev, { id: key, ...data }]
                            ));
                        } catch (error) {
                            console.error(`Error loading document ${key}:`, error);
                        }
                    }
                });
                await Promise.all(workers);
            } catch (error) {
                console.error('Error loading documents:', error);
            } finally {
                if (!cancelled) setIsLoadingDocs(false);
            }
        };

        loadDocuments();
        return () => {
            cancelled = true;
        };
    }, [db]);

    // Generate QR Code
    useEffect(() => {
        const generateQRCode = async () => {
            if (!selectedDocument) return;
            
            let qrData = `UNILIS UND DIPLOMA SANS - EDAP\n`;
            Object.keys(selectedDocument).forEach(key => {
                if (key !== 'createdAt' && selectedDocument[key]) {
                    qrData += `${key}: ${selectedDocument[key]}\n`;
                }
            });
            qrData += `Status: Document Verified\nIssued by: Université de Lisala - UNILIS`;
            
            try {
                const qrCodeUrl = await QRCode.toDataURL(qrData, {
                    width: 120,
                    margin: 1,
                    color: { dark: '#000000', light: '#FFFFFF' }
                });
                setQrCodeData(qrCodeUrl);
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };
        if (selectedDocument) generateQRCode();
    }, [selectedDocument]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'gradeType' && value !== 'Autre') {
            setFormData({ ...formData, [name]: value, gradeTypeCustom: '' });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validate required fields
        const needsEnglish = formData.language === 'English' || formData.language === 'Français + English';
        const requiredFields = ['noms', 'dateNaissance', 'lieuNaissance', 'pays', 'department', 'specialite', 'titreSuffix', 'gradeType', 'mention', 'anneeAcademique', 'dateEmission'];
        if (needsEnglish) {
            requiredFields.push('departmentEn', 'specialiteEn');
        }
        for (const field of requiredFields) {
            if (!formData[field] || formData[field].trim() === '') {
                alert(`${field} est obligatoire!`);
                return;
            }
        }
        // Validate custom grade type if selected
        if (formData.gradeType === 'Autre' && (!formData.gradeTypeCustom || formData.gradeTypeCustom.trim() === '')) {
            alert('Veuillez spécifier le type de diplôme personnalisé');
            return;
        }

        const documentId = isEditing
            ? editingId
            : `${formData.noms.replace(/\s+/g, '_')}_${Date.now()}`;
        const documentRefPath = ref(db, `unilis-und-diploma-sans/EDAP/${documentId}`);
        
        const dataToSave = isEditing 
            ? { ...formData }
            : { ...formData, createdAt: new Date().toISOString() };
        
        set(documentRefPath, dataToSave)
            .then(() => {
                const saved = { id: documentId, ...dataToSave };
                setDocuments((prev) => (
                    isEditing
                        ? prev.map((item) => (item.id === documentId ? saved : item))
                        : [...prev, saved]
                ));
                console.log(isEditing ? 'UNILIS UND diploma sans updated successfully' : 'UNILIS UND diploma sans saved successfully');
                message.success(isEditing ? 'Document modifié avec succès' : 'Document créé avec succès');
                resetForm();
            })
            .catch((error) => {
                console.error('Error saving UNILIS UND diploma sans:', error);
                message.error(isEditing ? 'Erreur lors de la modification' : 'Erreur lors de la création');
            });
    };

    const showDocumentModal = (document) => {
        setSelectedDocument(document);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedDocument(null);
    };

    const resetForm = () => {
        setFormData({
            noms: '',
            dateNaissance: '',
            lieuNaissance: '',
            province: '',
            pays: '',
            department: 'Sciences Juridiques',
            specialite: 'Droit des Affaires et Fiscalité',
            departmentEn: '',
            specialiteEn: '',
            titreSuffix: 'DE DOCTORAT',
            englishTitle: '',
            gradeType: 'Doctorat',
            gradeTypeCustom: '',
            mention: 'Très Honorable',
            anneeAcademique: '',
            numeroDoc: '',
            dateEmission: new Date().toISOString().split('T')[0],
            language: 'Français + English'
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEditDocument = (document) => {
        setFormData({
            noms: document.noms || '',
            dateNaissance: document.dateNaissance || '',
            lieuNaissance: document.lieuNaissance || '',
            province: document.province || '',
            pays: document.pays || '',
            department: document.department || 'Sciences Juridiques',
            specialite: document.specialite || 'Droit des Affaires et Fiscalité',
            departmentEn: document.departmentEn || '',
            specialiteEn: document.specialiteEn || '',
            titreSuffix: document.titreSuffix || 'DE DOCTORAT',
            englishTitle: document.englishTitle || '',
            gradeType: document.gradeType || 'Doctorat',
            gradeTypeCustom: document.gradeTypeCustom || '',
            mention: document.mention || 'Très Honorable',
            anneeAcademique: document.anneeAcademique || '',
            numeroDoc: document.numeroDoc || '',
            dateEmission: document.dateEmission || new Date().toISOString().split('T')[0],
            language: document.language || 'Français + English'
        });
        setIsEditing(true);
        setEditingId(document.id);
        
        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteDocument = (id) => {
        remove(ref(db, `unilis-und-diploma-sans/EDAP/${id}`))
            .then(() => {
                setDocuments((prev) => prev.filter((item) => item.id !== id));
                message.success('Document supprimé avec succès');
            })
            .catch((error) => {
                console.error('Error deleting document:', error);
                message.error('Erreur lors de la suppression');
            });
    };

    const formatDateWithMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const months = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const downloadPDF = async () => {
        if (!selectedDocument) return;

        const element = document.getElementById('unilis-und-diploma-sans-document');
        if (!element) {
            console.error('Document element not found');
            return;
        }

        try {
            const pdf = new jsPDF('l', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth(); // 842pt
            const pageHeight = pdf.internal.pageSize.getHeight(); // 595pt

            // Get all document pages
            const pages = element.querySelectorAll('.unilis-und-diploma-page');
            
            if (pages.length === 0) {
                message.error('Aucune page trouvée');
                return;
            }

            // Process each page individually (like bulletin.js)
            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                
                // Ensure white background for capture
                page.style.backgroundColor = 'white';

                // Wait for images to load
                const images = page.querySelectorAll('img');
                await Promise.all(Array.from(images).map(img => new Promise((resolve) => {
                    if (img.complete) resolve();
                    else { img.onload = resolve; img.onerror = resolve; }
                })));

                await new Promise(resolve => setTimeout(resolve, 400));

                const canvas = await html2canvas(page, {
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: '#FFFFFF',
                    windowWidth: page.scrollWidth,
                    windowHeight: page.scrollHeight
                });

                // Calculate scaling to fit page properly
                const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
                const w = canvas.width * ratio;
                const h = canvas.height * ratio;
                const x = (pageWidth - w) / 2; // Center horizontally
                const y = 20; // Small top margin

                if (i > 0) pdf.addPage();
                
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, w, h);
            }

            pdf.save(`${selectedDocument.noms?.replace(/\s+/g, '_') || 'document'}_UNILIS_UND_diploma_sans.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Erreur lors de la génération du PDF');
        }
    };

    return (
        <div className="unilis-und-diploma-container">
            <div className="unilis-und-form-section">
                <div style={{ textAlign: 'center' }}>
                    <img src={edaplogo} alt="Logo UNILIS" className="unilis-und-form-logo" />
                    <h2>Formulaire de Diplôme UND Sans - Université de Lisala</h2>
                </div>

                <form onSubmit={handleSubmit} className="unilis-und-form">
                    {/* Informations personnelles */}
                    <div className="unilis-und-form-section-title">Informations personnelles</div>
                    <div className="unilis-und-form-row">
                        <input
                            type="text"
                            name="noms"
                            value={formData.noms}
                            onChange={handleInputChange}
                            placeholder="Nom complet"
                            required
                            className="unilis-und-form-input"
                        />
                        <input
                            type="date"
                            name="dateNaissance"
                            value={formData.dateNaissance}
                            onChange={handleInputChange}
                            required
                            className="unilis-und-form-input"
                        />
                    </div>

                    <div className="unilis-und-form-row">
                        <input
                            type="text"
                            name="lieuNaissance"
                            value={formData.lieuNaissance}
                            onChange={handleInputChange}
                            placeholder="Lieu de naissance"
                            required
                            className="unilis-und-form-input"
                        />
                        <input
                            type="text"
                            name="province"
                            value={formData.province}
                            onChange={handleInputChange}
                            placeholder="Province (optionnel)"
                            className="unilis-und-form-input"
                        />
                    </div>

                    <div className="unilis-und-form-row">
                        <input
                            type="text"
                            name="pays"
                            value={formData.pays}
                            onChange={handleInputChange}
                            placeholder="Pays"
                            required
                            className="unilis-und-form-input"
                        />
                    </div>

                    {/* Informations du document */}
                    <div className="unilis-und-form-section-title">Informations du document</div>
                    <div className="unilis-und-form-row">
                        <input
                            type="text"
                            name="titreSuffix"
                            value={formData.titreSuffix}
                            onChange={handleInputChange}
                            placeholder="Titre du diplôme (ex: DE DOCTORAT, D'HABILITATION)"
                            required
                            className="unilis-und-form-input"
                        />
                        <input
                            type="text"
                            name="englishTitle"
                            value={formData.englishTitle}
                            onChange={handleInputChange}
                            placeholder="English title (will appear as [title] CERTIFICATE)"
                            required
                            className="unilis-und-form-input"
                        />
                    </div>

                    <div className="unilis-und-form-row">
                        <input
                            type="text"
                            name="numeroDoc"
                            value={formData.numeroDoc}
                            onChange={handleInputChange}
                            placeholder="Numéro du document (optionnel)"
                            className="unilis-und-form-input"
                        />
                    </div>

                    <div className="unilis-und-form-row">
                        <input
                            type="date"
                            name="dateEmission"
                            value={formData.dateEmission}
                            onChange={handleInputChange}
                            required
                            className="unilis-und-form-input"
                        />
                        <select
                            name="language"
                            value={formData.language}
                            onChange={handleInputChange}
                            required
                            className="unilis-und-form-input"
                        >
                            <option value="Français + English">Français + English (2 pages)</option>
                            <option value="Français">Français (1 page)</option>
                            <option value="English">English (1 page)</option>
                        </select>
                    </div>

                    {/* Informations académiques */}
                    <div className="unilis-und-form-section-title">Informations académiques</div>
                    <div className="unilis-und-form-row">
                        <input
                            type="text"
                            name="department"
                            value={formData.department}
                            onChange={handleInputChange}
                            placeholder="Département (ex: Sciences Juridiques)"
                            required
                            className="unilis-und-form-input"
                        />
                        <input
                            type="text"
                            name="specialite"
                            value={formData.specialite}
                            onChange={handleInputChange}
                            placeholder="Spécialité"
                            required
                            className="unilis-und-form-input"
                        />
                    </div>
                    {(formData.language === 'English' || formData.language === 'Français + English') && (
                        <div className="unilis-und-form-row">
                            <input
                                type="text"
                                name="departmentEn"
                                value={formData.departmentEn}
                                onChange={handleInputChange}
                                placeholder="Département en anglais"
                                required
                                className="unilis-und-form-input"
                            />
                            <input
                                type="text"
                                name="specialiteEn"
                                value={formData.specialiteEn}
                                onChange={handleInputChange}
                                placeholder="Spécialité en anglais"
                                required
                                className="unilis-und-form-input"
                            />
                        </div>
                    )}

                    <div className="unilis-und-form-row" style={{ display: formData.gradeType === 'Autre' ? 'block' : 'grid' }}>
                        <select
                            name="gradeType"
                            value={formData.gradeType}
                            onChange={handleInputChange}
                            required
                            className="unilis-und-form-input"
                        >
                            <option value="Doctorat">Doctorat</option>
                            <option value="Master">Master</option>
                            <option value="HDR">HDR</option>
                            <option value="Licence">Licence</option>
                            <option value="Autre">Autre</option>
                        </select>
                        {formData.gradeType === 'Autre' && (
                            <input
                                type="text"
                                name="gradeTypeCustom"
                                value={formData.gradeTypeCustom}
                                onChange={handleInputChange}
                                placeholder="Spécifiez le type de diplôme personnalisé"
                                required={formData.gradeType === 'Autre'}
                                className="unilis-und-form-input"
                                style={{ marginTop: '10px' }}
                            />
                        )}
                    </div>

                    <div className="unilis-und-form-row">
                        <select
                            name="mention"
                            value={formData.mention}
                            onChange={handleInputChange}
                            required
                            className="unilis-und-form-input"
                        >
                            <optgroup label="Doctorat / HDR">
                                <option value="Très Honorable avec Félicitations du Jury">Très Honorable avec Félicitations du Jury</option>
                                <option value="Très Honorable">Très Honorable</option>
                                <option value="Honorable">Honorable</option>
                            </optgroup>
                            <optgroup label="Master / Licence">
                                <option value="Très Bien">Très Bien</option>
                                <option value="Bien">Bien</option>
                                <option value="Assez Bien">Assez Bien</option>
                                <option value="Passable">Passable</option>
                            </optgroup>
                        </select>
                        <input
                            type="text"
                            name="anneeAcademique"
                            value={formData.anneeAcademique}
                            onChange={handleInputChange}
                            placeholder="Année académique (Ex: 2024-2025)"
                            required
                            className="unilis-und-form-input"
                        />
                    </div>

                    <button type="submit" className="unilis-und-submit-btn">
                        {isEditing ? 'Modifier le Diplôme UND Sans - UNILIS' : 'Créer le Diplôme UND Sans - UNILIS'}
                    </button>
                    {isEditing && (
                        <button 
                            type="button" 
                            onClick={resetForm}
                            className="unilis-und-submit-btn"
                            style={{ 
                                marginTop: '10px', 
                                background: 'linear-gradient(135deg, #6c757d, #5a6268)' 
                            }}
                        >
                            Annuler
                        </button>
                    )}
                </form>

                {/* Document List */}
                <div className="unilis-und-document-list">
                    {isLoadingDocs && <p className="unilis-und-loading">Chargement…</p>}
                    {documents.map((document) => (
                        <div key={document.id} className="unilis-und-document-item">
                            <button
                                className="unilis-und-document-toggle"
                                onClick={() => showDocumentModal(document)}
                            >
                                <Eye size={16} style={{ marginRight: '8px' }} />
                                {document.noms} - UNILIS UND Diploma Sans
                            </button>
                            <button
                                className="unilis-und-document-toggle"
                                onClick={() => handleEditDocument(document)}
                                style={{ 
                                    marginRight: '8px', 
                                    background: '#ffc107',
                                    color: '#000'
                                }}
                            >
                                <Edit size={16} style={{ marginRight: '8px' }} />
                                Modifier
                            </button>
                            <Popconfirm
                                title="Supprimer le document"
                                description="Êtes-vous sûr de vouloir supprimer ce document ?"
                                onConfirm={() => handleDeleteDocument(document.id)}
                                okText="Oui"
                                cancelText="Non"
                                okButtonProps={{ danger: true }}
                            >
                                <button className="unilis-und-delete-btn">
                                    <Delete size={15} />
                                </button>
                            </Popconfirm>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal */}
            <ConfigProvider>
                <Modal
                    title={<span style={{ color: 'white' }}>Aperçu - Diplôme UND Sans UNILIS</span>}
                    open={modalVisible}
                    onCancel={handleModalClose}
                    width="95vw"
                    style={{ top: 10 }}
                    centered={false}
                    maskClosable={true}
                    keyboard={true}
                    footer={[
                        <Button
                            key="edit"
                            onClick={() => {
                                if (selectedDocument) {
                                    handleEditDocument(selectedDocument);
                                    handleModalClose();
                                }
                            }}
                            style={{ fontWeight: 'bold' }}
                        >
                            Modifier
                        </Button>,
                        <Button
                            key="download"
                            type="primary"
                            icon={<Download size={16} />}
                            onClick={downloadPDF}
                            style={{
                                backgroundColor: '#28a745',
                                borderColor: '#28a745',
                                fontWeight: 'bold'
                            }}
                        >
                            Télécharger PDF
                        </Button>,
                        <Button
                            key="close"
                            onClick={handleModalClose}
                            style={{ fontWeight: 'bold' }}
                        >
                            Fermer
                        </Button>
                    ]}
                >
                    {selectedDocument && (
                        <div className="unilis-und-modal-content">
                            <UNILISUNDDiplomaSansDocument document={selectedDocument} qrCodeData={qrCodeData} language={selectedDocument.language || 'Français + English'} />
                        </div>
                    )}
                </Modal>
            </ConfigProvider>
        </div>
    );
};

// Document Component
const UNILISUNDDiplomaSansDocument = ({ document: docData, qrCodeData, language = 'Français + English' }) => {
    const showBoth = language === 'Français + English';
    const showFrench = language === 'Français' || showBoth;
    const showEnglish = language === 'English' || showBoth;

    return (
        <div id="unilis-und-diploma-sans-document" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'transparent',
            padding: '20px',
            width: 'fit-content',
            minWidth: '30cm',
            margin: '0 auto',
            overflowX: 'hidden'
        }}>
            {showFrench && (
                <UNILISUNDDiplomaSansPage document={docData} qrCodeData={qrCodeData} language="French" />
            )}
            {showEnglish && (
                <UNILISUNDDiplomaSansPage document={docData} qrCodeData={qrCodeData} language="English" />
            )}
        </div>
    );
};

// Single Page Component
const UNILISUNDDiplomaSansPage = ({ document: docData, qrCodeData, language = 'French' }) => {
    const formatDateWithMonthName = (dateStr, lang = 'fr') => {
        const date = new Date(dateStr);
        const monthsFr = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const monthsEn = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        const months = lang === 'en' ? monthsEn : monthsFr;
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const isEnglish = language === 'English';
    const departmentName = isEnglish ? (docData.departmentEn || docData.department) : docData.department;
    const specialiteName = isEnglish ? (docData.specialiteEn || docData.specialite) : docData.specialite;
    const pageId = `unilis-und-diploma-sans-${isEnglish ? 'english' : 'french'}`;

    // Translation mappings
    const translations = {
        'DIPLOME': isEnglish ? 'CERTIFICATE' : 'DIPLOME',
        'REPUBLIQUE DEMOCRATIQUE DU CONGO': isEnglish ? 'DEMOCRATIC REPUBLIC OF CONGO' : 'REPUBLIQUE DEMOCRATIQUE DU CONGO',
        'UNIVERSITE DE LISALA': isEnglish ? 'UNIVERSITY OF LISALA' : 'UNIVERSITE DE LISALA',
        'Vu la Loi-cadre n°14/004 du 11 février 2014 de l\'Enseignement National;': isEnglish 
            ? 'In view of Framework Law No. 14/004 of February 11, 2014 on National Education;'
            : 'Vu la Loi-cadre n°14/004 du 11 février 2014 de l\'Enseignement National;',
        'Vu l\'arrêté ministériel n°  133/MINESU/CAB.MIN/NM/RS/2006 du 25 avril 2006 portant création et prise en charge par le trésor public d\'un centre universitaire qualifié « Centre Universitaire de Lisala » ;': isEnglish
            ? 'In view of Ministerial Decree No. 133/MINESU/CAB.MIN/NM/RS/2006 of April 25, 2006 establishing and providing for a qualified university center "University Center of Lisala" by the public treasury;'
            : 'Vu l\'arrêté ministériel n°  133/MINESU/CAB.MIN/NM/RS/2006 du 25 avril 2006 portant création et prise en charge par le trésor public d\'un centre universitaire qualifié « Centre Universitaire de Lisala » ;',
        'Vu l\'arrêté ministériel n° 246/MINESU/CAB.MIN/MML/CB/JN/2010 du 02/12/2010, portant autonomisation du Centre Universitaire de Lisala en Université de Lisala ;': isEnglish
            ? 'In view of Ministerial Decree No. 246/MINESU/CAB.MIN/MML/CB/JN/2010 of December 2, 2010, granting autonomy to the University Center of Lisala as the University of Lisala;'
            : 'Vu l\'arrêté ministériel n° 246/MINESU/CAB.MIN/MML/CB/JN/2010 du 02/12/2010, portant autonomisation du Centre Universitaire de Lisala en Université de Lisala ;',
        'Vu les accords de partenariats de cotutelle internationale de thèses et Co diplomation signés entre le Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP) de l\'Université de Lisala en RDC, Groupe de Recherche Appliquée pour le Développement par l\'Innovation de l\'AFrique - GRADIAF International en France; Université IRGIB AFRICA au Benin et UNCR university;': isEnglish
            ? 'In view of the international co-supervision and co-diplomation partnership agreements signed between the Interuniversity Center for Multidisciplinary Research (CIREP) of the University of Lisala in DRC, Applied Research Group for Development through Innovation in Africa - GRADIAF International in France; IRGIB AFRICA University in Benin and UNCR university;'
            : 'Vu les accords de partenariats de cotutelle internationale de thèses et Co diplomation signés entre le Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP) de l\'Université de Lisala en RDC, Groupe de Recherche Appliquée pour le Développement par l\'Innovation de l\'AFrique - GRADIAF International en France; Université IRGIB AFRICA au Benin et UNCR university;',
        'Vu les procès-verbaux du Jury attestant que l\'intéressé a satisfait au contrôle des compétences prévu par les textes légaux et réglementaires;': isEnglish
            ? 'In view of the Jury minutes attesting that the interested party has satisfied the competency assessment provided for by legal and regulatory texts;'
            : 'Vu les procès-verbaux du Jury attestant que l\'intéressé a satisfait au contrôle des compétences prévu par les textes légaux et réglementaires;',
        'Le diplôme de': isEnglish ? 'The degree of' : 'Le diplôme de',
        'en': isEnglish ? 'in' : 'en',
        'Spécialité:': isEnglish ? 'Specialty:' : 'Spécialité:',
        'est délivré à': isEnglish ? 'is awarded to' : 'est délivré à',
        'né(e) le': isEnglish ? 'born on' : 'né(e) le',
        'à': isEnglish ? 'in' : 'à',
        'Province du': isEnglish ? 'Province of' : 'Province du',
        'au titre de l\'année académique': isEnglish ? 'for the academic year' : 'au titre de l\'année académique',
        'avec la mention': isEnglish ? 'with the distinction' : 'avec la mention',
        'et lui confère le grade de': isEnglish ? 'and confers upon him/her the degree of' : 'et lui confère le grade de',
        'pour en jouir avec les droits et prérogatives qui y sont attachés.': isEnglish
            ? 'to enjoy with the rights and prerogatives attached thereto.'
            : 'pour en jouir avec les droits et prérogatives qui y sont attachés.',
        'Fait à Lisala, le': isEnglish ? 'Done in Lisala, on' : 'Fait à Lisala, le',
        'Le Recteur': isEnglish ? 'The Rector' : 'Le Recteur',
        'Directeur de l\'École Doctorale/UNCR University': isEnglish ? 'Director of the Doctoral School/UNCR University' : 'Directeur de l\'École Doctorale/UNCR University',
        'Directeur Général du CIREP': isEnglish ? 'Director General of CIREP' : 'Directeur Général du CIREP'
    };

    const getMentionTranslation = (mention) => {
        if (isEnglish) {
            const mentionMap = {
                'Très Honorable avec Félicitations du Jury': 'Very Honorable with Jury Congratulations',
                'Très Honorable': 'Very Honorable',
                'Honorable': 'Honorable',
                'Très Bien': 'Very Good',
                'Bien': 'Good',
                'Assez Bien': 'Fairly Good',
                'Passable': 'Pass'
            };
            return mentionMap[mention] || mention;
        }
        return mention;
    };

    const getGradeTranslation = (gradeType) => {
        if (isEnglish) {
            if (gradeType === 'Autre') return docData.gradeTypeCustom || gradeType;
            const gradeMap = {
                'Doctorat': 'Doctorate (PhD)',
                'Master': 'Master',
                'HDR': 'HDR',
                'Licence': 'Bachelor'
            };
            return gradeMap[gradeType] || gradeType;
        }
        if (gradeType === 'Autre') return docData.gradeTypeCustom || gradeType;
        if (gradeType === 'Doctorat') return 'Doctorat (PhD)';
        return gradeType;
    };

    return (
        <div className="unilis-und-diploma-page" id={pageId} style={{
            width: '29.7cm',
            height: '21cm',
            padding: '2cm',
            margin: '0 auto 30px auto',
            background: 'white',
            color: 'black',
            position: 'relative',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            lineHeight: 1.4,
            pageBreakAfter: 'always',
            pageBreakInside: 'avoid',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            fontFamily: "'Times New Roman', serif"
        }}>

            {/* Header */}
            <div className="unilis-und-header">
                <div className="unilis-und-header-row">
                    <div className="unilis-und-left-logo">
                        <img src={edaplogo} alt="Logo Left" className="unilis-und-logo" />
                    </div>
                    <div className="unilis-und-header-center">
                        <h1 className="unilis-und-country">{translations['REPUBLIQUE DEMOCRATIQUE DU CONGO']}</h1>
                        <h2 className="unilis-und-university">{translations['UNIVERSITE DE LISALA']}</h2>
                        <p className="unilis-und-address" style={{ marginTop: '8px', fontSize: '12px' }}>{isEnglish ? '20 Aérodrom avenue, District 3C, Bolikango Municipality, City of lisala, Mongala province' : 'Avenue Aérodrome n° 20 , Quartier 3C, Commune de Bolikango, Ville de Lisala, Province de la Mongala'}</p>
                        <p className="unilis-und-address" style={{ fontSize: '12px' }}>P.O. Box 9289 Kinshasa I</p>
                        <p className="unilis-und-contact" style={{ fontSize: '12px' }}>Web: www.universitedelisala.ac.cd ; Email: info@universitedelisala.ad.cd</p>
                    </div>
                    <div className="unilis-und-right-logo">
                        <img src={crlogo} alt="Logo Right" className="unilis-und-logo" />
                    </div>
                </div>
            </div>

            {/* Title Section */}
            <div className="unilis-und-title-section">
                <div className="unilis-und-title-row">
                    <div className="unilis-und-title-spacer"></div>
                    <h1 className="unilis-und-diploma-title">
                        {isEnglish 
                            ? `${(docData.englishTitle || '').toUpperCase()} ${translations['DIPLOME']}`
                            : `${translations['DIPLOME']} ${docData.titreSuffix ? docData.titreSuffix.toUpperCase() : 'DE DOCTORAT'}`
                        }
                    </h1>
                    <div className="unilis-und-number-box">
                        <span className="number-label">N°</span>
                        <span className="number-value">{docData.numeroDoc || ''}</span>
                    </div>
                </div>
            </div>

            {/* Document Body */}
            <div className="unilis-und-document-body">
                <div className="unilis-und-legal-references">
                    <p className="vu-clause">{translations['Vu la Loi-cadre n°14/004 du 11 février 2014 de l\'Enseignement National;']}</p>
                    <p className="vu-clause">{translations['Vu l\'arrêté ministériel n°  133/MINESU/CAB.MIN/NM/RS/2006 du 25 avril 2006 portant création et prise en charge par le trésor public d\'un centre universitaire qualifié « Centre Universitaire de Lisala » ;']}</p>
                    <p className="vu-clause">{translations['Vu l\'arrêté ministériel n° 246/MINESU/CAB.MIN/MML/CB/JN/2010 du 02/12/2010, portant autonomisation du Centre Universitaire de Lisala en Université de Lisala ;']}</p>
                    <p className="vu-clause">{translations['Vu les accords de partenariats de cotutelle internationale de thèses et Co diplomation signés entre le Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP) de l\'Université de Lisala en RDC, Groupe de Recherche Appliquée pour le Développement par l\'Innovation de l\'AFrique - GRADIAF International en France; Université IRGIB AFRICA au Benin et UNCR university;']}</p>
                    <p className="vu-clause">{translations['Vu les procès-verbaux du Jury attestant que l\'intéressé a satisfait au contrôle des compétences prévu par les textes légaux et réglementaires;']}</p>
                </div>

                <div className="unilis-und-diploma-declaration">
                    <p className="main-declaration">
                        {translations['Le diplôme de']} <strong>{getGradeTranslation(docData.gradeType)}</strong> {translations['en']} <strong>{departmentName}</strong> {translations['Spécialité:']} <strong>{specialiteName}</strong> {translations['est délivré à']} <strong>{docData.noms}</strong> {translations['né(e) le']} <strong>{formatDateWithMonthName(docData.dateNaissance, isEnglish ? 'en' : 'fr')}</strong> {translations['à']} <strong>{docData.lieuNaissance}</strong>{docData.province ? ` ${translations['Province du']} ${docData.province}` : ''}/<strong>{docData.pays}</strong> {translations['au titre de l\'année académique']} <strong>{docData.anneeAcademique}</strong> {translations['avec la mention']} <strong>{getMentionTranslation(docData.mention)}</strong> {translations['et lui confère le grade de']} {getGradeTranslation(docData.gradeType)}, {translations['pour en jouir avec les droits et prérogatives qui y sont attachés.']}
                    </p>
                </div>

                <div className="unilis-und-emission-date">
                    <p>{translations['Fait à Lisala, le']} <strong>{formatDateWithMonthName(docData.dateEmission, isEnglish ? 'en' : 'fr')}</strong></p>
                </div>
            </div>

            {/* Signatures */}
            <div className="unilis-und-signatures">
                <div className="unilis-und-signature-row">
                    <div className="unilis-und-signature-item">
                        <img src={rectorSig} alt="Rector Signature" style={{ width: '120px', height: '60px', objectFit: 'contain', marginBottom: '8px' }} />
                        <hr className="signature-line-separator" />
                        <p className="signature-name">Professeur Abbé Donat TEBAKABE Alomo</p>
                        <p className="signature-title">{translations['Le Recteur']}</p>
                    </div>
                    <div className="unilis-und-signature-item">
                        <img src={perezSig} alt="Perez Signature" style={{ width: '120px', height: '60px', objectFit: 'contain', marginBottom: '8px' }} />
                        <hr className="signature-line-separator" />
                        <p className="signature-name">Pr Jean Luc Perez</p>
                        <p className="signature-title">{translations['Directeur de l\'École Doctorale/UNCR University']}</p>
                    </div>
                    <div className="unilis-und-signature-item">
                        <img src={bapSig} alt="Perez Signature" style={{ width: '120px', height: '60px', objectFit: 'contain', marginBottom: '8px' }} />
                        <hr className="signature-line-separator" />
                        <p className="signature-name">Pr NIZEYIMANA Jean Baptiste</p>
                        <p className="signature-title">{translations['Directeur Général du CIREP']}</p>
                    </div>
                    {qrCodeData && (
                        <div className="unilis-und-qr-section">
                            <img
                                src={qrCodeData}
                                alt="QR Code - Document Verification"
                                className="qr-code"
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    border: '1px solid #000'
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UNILISUNDDiplomaSansTemplate;

